"use client";

import type {
  AvailableVariants,
  SingleProductByVariant,
} from "@/backend/services/products";
import { Button } from "@/components/ui/button";
import { useRouter, useSearchParams } from "next/navigation";
import {
  parseAsString,
  useQueryState,
  SetValues,
  useQueryStates,
  Parser,
  UseQueryStatesKeysMap,
  type ParserBuilder,
} from "nuqs";
import { useEffect, useMemo } from "react";

function Options({
  options,
  available,
  selected,
}: {
  options: {
    id: string;
    name: string;
    values: {
      id: string;
      value: string;
      label: string;
    }[];
  }[];
  selected?: {
    option: string;
    value: string;
  }[];
  available: AvailableVariants | null;
}) {
  const router = useRouter();
  const qryParams = useSearchParams();
  const optionQueryParams = options.filter(
    (option) =>
      qryParams.get(option.name) ??
      selected?.some((s) => s.option === option.name),
  );

  const optionMap = optionQueryParams.reduce(
    (acc, option) => {
      acc[option.name] = parseAsString.withOptions({
        shallow: false,
        clearOnDefault: true,
      }) as ParserBuilder<string>;

      return acc;
    },
    {} as {
      [key: string]: ParserBuilder<string>;
    },
  );

  const [, setQuery] = useQueryStates(optionMap);

  const selectedOptions = optionQueryParams.map((option) => {
    return {
      optionId: option.id,
      option: option.name,
      value: selected?.find((s) => s.option === option.name)?.value,
    };
  });

  const selectedOptionsLength = optionQueryParams.length;

  return (
    <section className="flex flex-col gap-2">
      {options.map((option, idx) => {
        return (
          <div key={option.id}>
            <h3 className="pb-1 font-semibold">{option.name}</h3>
            <ul className="flex flex-row flex-wrap items-center justify-start gap-2">
              {option.values.map((value) => {
                const prevOptions = selectedOptions.slice(0, idx);

                const possibleVariant = [
                  ...prevOptions,
                  {
                    optionId: option.id,
                    option: option.name,
                    value: value.value,
                  },
                ];
                const availableVariant = available?.find((variant) => {
                  return possibleVariant.every((selectedOption) => {
                    return variant.optionValues.find((ov) => {
                      return (
                        ov.optionId === selectedOption.optionId &&
                        ov.value === selectedOption.value
                      );
                    });
                  });
                });

                const isAvailable = !!availableVariant?.isAvailable;
                const exists = !!availableVariant;

                const fromSelected = selected?.find(
                  (opt) => opt.option === option.name,
                );

                return (
                  <OptionValue
                    key={value.id}
                    option={option}
                    value={value}
                    optionIndex={idx}
                    totalOptions={options.length}
                    selectedOptionsLength={selectedOptionsLength}
                    isSelected={fromSelected?.value === value.value}
                    exists={exists}
                    onOptionChange={(option, value) => {
                      // deselect all options after this one
                      const nextOptions = options.slice(idx + 1);
                      const remainingOptions = selectedOptions.slice(
                        0,
                        idx + 1,
                      );

                      setQuery({
                        ...remainingOptions.reduce((acc, option) => {
                          acc[option.option] = option.value;
                          return acc;
                        }, {} as any),
                        ...nextOptions.reduce((acc, option) => {
                          acc[option.name] = null;
                          return acc;
                        }, {} as any),
                        [option]: value,
                      });
                    }}
                    available={isAvailable}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

const OptionValue = ({
  option,
  value,
  available,
  optionIndex,
  totalOptions,
  onOptionChange,
  selectedOptionsLength,
  isSelected,
  exists = false,
}: {
  option: { name: string };
  value: { id: string; value: string; label: string };
  available?: boolean;
  onOptionChange?: (option: string, value: string | null) => void;
  totalOptions: number;
  optionIndex: number;
  selectedOptionsLength: number;
  isSelected: boolean;
  exists?: boolean;
}) => {
  const [query, setQuery] = useQueryState<string>(
    option.name,
    parseAsString.withOptions({
      throttleMs: 400,
      shallow: false,
      clearOnDefault: true,
    }),
  );

  const isDisabled = selectedOptionsLength < optionIndex && !isSelected;

  return (
    <Button
      variant={"outline"}
      size={"lg"}
      disabled={isDisabled || !exists}
      className={
        isSelected
          ? "border border-orange-300 bg-orange-50/75 hover:bg-orange-200"
          : ""
      }
      onClick={() => {
        if (!isSelected) {
          setQuery(value.value);
          onOptionChange?.(option.name, value.value);
        } else {
          setQuery(null);
          onOptionChange?.(option.name, null);
        }
      }}
    >
      {value.label}
    </Button>
  );
};

export default Options;
